import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import surveyService from '../../../services/survey.service';
import { confirmAction, confirmDelete } from '../../../utils/ecxSwal';
import SurveyRowActions from './SurveyRowActions';
import './Surveys.css';

const escapeHtml = (value = '') =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const STATUS_OPTIONS = [
    { value: '', label: 'All statuses', icon: 'bi-funnel' },
    { value: 'DRAFT', label: 'Draft', icon: 'bi-pencil-square' },
    { value: 'ACTIVE', label: 'Active', icon: 'bi-play-circle' },
    { value: 'CLOSED', label: 'Closed', icon: 'bi-lock' },
    { value: 'ARCHIVED', label: 'Archived', icon: 'bi-archive' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const STATUS_BADGE = {
    DRAFT: 'surveys-status--draft',
    ACTIVE: 'surveys-status--active',
    CLOSED: 'surveys-status--closed',
    ARCHIVED: 'surveys-status--archived',
};

const formatStatus = (status) => {
    if (!status) return '—';
    return String(status)
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return '—';
    if (startDate && endDate) return `${startDate} – ${endDate}`;
    return startDate || endDate;
};

const isDoctorAssigned = (survey) =>
    survey?.assignmentScope === 'DOCTOR' && Boolean(survey?.assignedUserId);

const groupSurveysForList = (surveyList = []) => {
    const clinicSurveys = [];
    const doctorMap = new Map();

    surveyList.forEach((survey) => {
        if (isDoctorAssigned(survey)) {
            const key = survey.assignedUserId;
            const label = survey.assignedUserName || 'Doctor';
            if (!doctorMap.has(key)) {
                doctorMap.set(key, { key, label, surveys: [] });
            }
            doctorMap.get(key).surveys.push(survey);
            return;
        }
        clinicSurveys.push(survey);
    });

    const doctorSections = Array.from(doctorMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );

    return { clinicSurveys, doctorSections };
};

function Surveys() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const navigate = useNavigate();

    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [showInstallModal, setShowInstallModal] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templateSearch, setTemplateSearch] = useState('');
    const [appliedTemplateSearch, setAppliedTemplateSearch] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [installing, setInstalling] = useState(false);

    const fetchSurveys = useCallback(
        async (options = {}) => {
            const { silent = false } = options;
            if (!clinicId) return;

            if (!silent) setLoading(true);

            try {
                const res = await surveyService.getClinicSurveys(clinicId, pageNo, pageSize, {
                    sortBy,
                    sortOrder,
                    searchField: 'title',
                    searchTerm: appliedSearch,
                    statusFilter,
                });
                setSurveys(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
            } catch (err) {
                console.error(err);
                toast.error('Failed to load surveys');
                setSurveys([]);
                setTotalPages(0);
                setTotalElements(0);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [clinicId, pageNo, pageSize, sortBy, sortOrder, appliedSearch, statusFilter]
    );

    useEffect(() => {
        fetchSurveys();
    }, [fetchSurveys]);

    const loadTemplates = useCallback(async () => {
        if (!clinicId) return;
        setTemplatesLoading(true);
        try {
            const res = await surveyService.getSurveyTemplates(clinicId, 0, 50, {
                sortBy: 'title',
                sortOrder: 'ASC',
                searchField: 'title',
                searchTerm: appliedTemplateSearch,
            });
            setTemplates(res.data.content || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load survey templates');
            setTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    }, [clinicId, appliedTemplateSearch]);

    useEffect(() => {
        if (showInstallModal) {
            loadTemplates();
        }
    }, [showInstallModal, loadTemplates]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchSurveys({ silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNo(0);
        setAppliedSearch(searchTerm.trim());
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setStatusFilter('');
        setPageNo(0);
    };

    const handleStatusFilter = (value) => {
        setStatusFilter(value);
        setPageNo(0);
    };

    const handleInstall = async () => {
        const selected = templates.find((t) => t.id === selectedTemplateId);
        if (!selected) return;

        const confirm = await confirmAction({
            title: 'Install survey template?',
            html: `<p>"<strong>${escapeHtml(selected.title || 'Untitled')}</strong>" will be copied to this clinic as a <strong>Draft</strong> survey.</p>`,
            iconClass: 'bi bi-download',
            confirmText: 'Install',
        });

        if (!confirm.isConfirmed) return;

        setInstalling(true);
        try {
            await surveyService.installSurveyTemplate(clinicId, selected.id);
            toast.success('Survey installed');
            setShowInstallModal(false);
            setSelectedTemplateId('');
            if (pageNo === 0) {
                await fetchSurveys({ silent: true });
            } else {
                setPageNo(0);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to install survey');
        } finally {
            setInstalling(false);
        }
    };

    const handleDelete = async (survey) => {
        const confirm = await confirmDelete({
            title: 'Delete survey?',
            itemName: survey.title || 'This survey',
            description: 'This survey will be permanently removed.',
        });

        if (!confirm.isConfirmed) return;

        try {
            await surveyService.deleteClinicSurvey(clinicId, survey.id);
            toast.success('Survey deleted');
            if (pageNo === 0) {
                await fetchSurveys({ silent: true });
            } else {
                setPageNo(0);
            }
        } catch {
            toast.error('Failed to delete survey');
        }
    };

    const handleActivate = async (survey) => {
        try {
            const res = await surveyService.getClinicSurveyById(clinicId, survey.id);
            const payload = {
                survey: { ...res.data.survey, status: 'ACTIVE' },
                questions: res.data.questions || [],
            };
            await surveyService.updateClinicSurvey(clinicId, survey.id, payload);
            await fetchSurveys({ silent: true });
        } catch {
            toast.error('Failed to activate survey');
        }
    };

    const handleDeactivate = async (survey) => {
        try {
            const res = await surveyService.getClinicSurveyById(clinicId, survey.id);
            const payload = {
                survey: { ...res.data.survey, status: 'CLOSED' },
                questions: res.data.questions || [],
            };
            await surveyService.updateClinicSurvey(clinicId, survey.id, payload);
            await fetchSurveys({ silent: true });
        } catch {
            toast.error('Failed to deactivate survey');
        }
    };

    const openInstallModal = () => {
        setTemplateSearch('');
        setAppliedTemplateSearch('');
        setSelectedTemplateId('');
        setShowInstallModal(true);
    };

    const description = useMemo(() => {
        const parts = [`${totalElements.toLocaleString()} survey${totalElements === 1 ? '' : 's'}`];
        if (statusFilter) parts.push(formatStatus(statusFilter));
        if (appliedSearch) parts.push(`“${appliedSearch}”`);
        return parts.join(' · ');
    }, [totalElements, statusFilter, appliedSearch]);

    const toolbarMeta = useMemo(
        () => ({
            totalLabel: `${totalElements.toLocaleString()} total`,
            pageLabel: `Page ${surveys.length ? pageNo + 1 : 0}${totalPages ? ` of ${totalPages}` : ''}`,
        }),
        [totalElements, surveys.length, pageNo, totalPages]
    );

    const hasFilters = Boolean(appliedSearch || statusFilter || searchTerm);

    const { clinicSurveys, doctorSections } = useMemo(
        () => groupSurveysForList(surveys),
        [surveys]
    );

    const renderSurveyRow = (survey) => (
        <tr key={survey.id}>
            <td>
                <div className="fw-normal">{survey.title || 'Untitled survey'}</div>
                {survey.description ? (
                    <div className="text-muted small surveys-desc">{survey.description}</div>
                ) : null}
            </td>
            <td>
                <span className={`surveys-status ${STATUS_BADGE[survey.status] || STATUS_BADGE.DRAFT}`}>
                    {formatStatus(survey.status)}
                </span>
            </td>
            <td className="text-muted small">{survey.category || '—'}</td>
            <td className="text-muted small">{formatDateRange(survey.startDate, survey.endDate)}</td>
            <td className="text-end surveys-actions-cell">
                <SurveyRowActions
                    survey={survey}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivate}
                    onDelete={handleDelete}
                />
            </td>
        </tr>
    );

    const renderGroupHeader = (key, icon, label, count) => (
        <tr key={key} className="surveys-group-row">
            <td colSpan={5}>
                <div className="surveys-group-label">
                    <i className={`bi ${icon}`} />
                    <span>{label}</span>
                    <span className="surveys-group-count">
                        {count} survey{count === 1 ? '' : 's'}
                    </span>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="ecx-page surveys-page ecx-page--full-width">
            <SubHeader
                title="Surveys"
                description={description}
                icon="bi-clipboard-check"
                containerClass="w-100 px-4"
            >
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleRefresh}
                        disabled={loading || isRefreshing}
                        title="Refresh surveys"
                    >
                        <i className={`bi bi-arrow-clockwise me-1${isRefreshing ? ' surveys-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm rounded-pill"
                        onClick={openInstallModal}
                    >
                        <i className="bi bi-download me-1" />
                        Install template
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm rounded-pill"
                        onClick={() => navigate('/surveys/create-survey')}
                    >
                        <i className="bi bi-plus-circle me-1" />
                        Create survey
                    </button>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel surveys-panel">
                    <div className="surveys-filters">
                        <form className="surveys-filters__row" onSubmit={handleSearchSubmit}>
                            <div className="surveys-filters__search input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted" />
                                </span>
                                <input
                                    type="search"
                                    className="form-control form-control-sm border-start-0"
                                    placeholder="Search by title…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={loading}
                                />
                                <button type="submit" className="btn btn-sm btn-accent" disabled={loading}>
                                    Search
                                </button>
                            </div>

                            <select
                                className="form-select form-select-sm surveys-filters__select"
                                value={`${sortBy}:${sortOrder}`}
                                onChange={(e) => {
                                    const [nextSort, nextOrder] = e.target.value.split(':');
                                    setSortBy(nextSort);
                                    setSortOrder(nextOrder);
                                    setPageNo(0);
                                }}
                                disabled={loading}
                            >
                                <option value="createdAt:DESC">Newest first</option>
                                <option value="createdAt:ASC">Oldest first</option>
                                <option value="title:ASC">Title A–Z</option>
                                <option value="title:DESC">Title Z–A</option>
                                <option value="status:ASC">Status</option>
                            </select>

                            {hasFilters && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill"
                                    onClick={handleClearFilters}
                                    disabled={loading}
                                >
                                    Clear
                                </button>
                            )}
                        </form>

                        <div className="surveys-filters__row">
                            <div className="surveys-filters__group">
                                <span className="surveys-filters__label">Status</span>
                                <div className="surveys-pills">
                                    {STATUS_OPTIONS.map((option) => (
                                        <button
                                            key={option.value || 'all'}
                                            type="button"
                                            className={`surveys-pill${statusFilter === option.value ? ' is-active' : ''}`}
                                            onClick={() => handleStatusFilter(option.value)}
                                            disabled={loading}
                                        >
                                            <i className={`bi ${option.icon}`} />
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__header border-top-0">
                        <div className="surveys-toolbar">
                            <div className="surveys-toolbar__meta">
                                <h5 className="mb-0 fw-normal text-dark">Surveys</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul" />
                                    {toolbarMeta.totalLabel}
                                </span>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-layers" />
                                    {toolbarMeta.pageLabel}
                                </span>
                            </div>

                            <div className="surveys-toolbar__controls">
                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setPageNo(0);
                                        }}
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body">
                        <div className="table-responsive surveys-table-wrap">
                            <table className="table surveys-table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Survey</th>
                                        <th>Status</th>
                                        <th>Category</th>
                                        <th>Dates</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <tr key={`sk-${i}`} className="surveys-skeleton-row">
                                                <td colSpan={5}>
                                                    <div className="surveys-skeleton-bar" />
                                                </td>
                                            </tr>
                                        ))
                                    ) : surveys.length === 0 ? (
                                        <tr>
                                            <td colSpan={5}>
                                                <div className="ecx-page-empty">
                                                    <span className="ecx-page-empty__icon">
                                                        <i className="bi bi-clipboard-check" />
                                                    </span>
                                                    <div className="fw-normal mb-1">No surveys yet</div>
                                                    <div className="small text-muted mb-3">
                                                        Install a template or create a survey from scratch.
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary rounded-pill"
                                                            onClick={() => navigate('/surveys/create-survey')}
                                                        >
                                                            Create survey
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary rounded-pill"
                                                            onClick={openInstallModal}
                                                        >
                                                            Install template
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        <>
                                            {clinicSurveys.length > 0 && (
                                                <>
                                                    {renderGroupHeader(
                                                        'group-clinic',
                                                        'bi-building',
                                                        'Entire clinic',
                                                        clinicSurveys.length
                                                    )}
                                                    {clinicSurveys.map(renderSurveyRow)}
                                                </>
                                            )}
                                            {doctorSections.map((section) => (
                                                <React.Fragment key={section.key}>
                                                    {renderGroupHeader(
                                                        `group-doctor-${section.key}`,
                                                        'bi-person-badge',
                                                        section.label,
                                                        section.surveys.length
                                                    )}
                                                    {section.surveys.map(renderSurveyRow)}
                                                </React.Fragment>
                                            ))}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {!loading && totalPages > 0 && (
                            <div className="surveys-pagination">
                                <Pagination
                                    currentPage={pageNo}
                                    totalPages={totalPages}
                                    totalElements={totalElements}
                                    pageSize={pageSize}
                                    onPageChange={setPageNo}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showInstallModal && (
                <div className="surveys-modal-backdrop" role="dialog" aria-modal="true">
                    <div className="surveys-modal">
                        <div className="surveys-modal__header">
                            <div>
                                <h2 className="h5 mb-0 fw-normal">Install survey template</h2>
                                <p className="small text-muted mb-0 mt-1">
                                    Copy a global template into this clinic as a draft
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                disabled={installing}
                                onClick={() => setShowInstallModal(false)}
                                aria-label="Close"
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>
                        <form
                            className="surveys-modal__search"
                            onSubmit={(e) => {
                                e.preventDefault();
                                setSelectedTemplateId('');
                                setAppliedTemplateSearch(templateSearch.trim());
                            }}
                        >
                            <div className="input-group input-group-sm flex-grow-1">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted" />
                                </span>
                                <input
                                    type="search"
                                    className="form-control form-control-sm border-start-0"
                                    placeholder="Search templates…"
                                    value={templateSearch}
                                    onChange={(e) => setTemplateSearch(e.target.value)}
                                />
                            </div>
                            <button type="submit" className="btn btn-sm btn-outline-secondary rounded-pill">
                                Search
                            </button>
                        </form>
                        <div className="surveys-modal__body">
                            {templatesLoading ? (
                                <div className="text-center text-muted py-4">Loading templates…</div>
                            ) : templates.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    {appliedTemplateSearch
                                        ? 'No templates match your search.'
                                        : 'No survey templates available.'}
                                </div>
                            ) : (
                                <div className="surveys-template-list">
                                    {templates.map((template) => (
                                        <label
                                            key={template.id}
                                            className={`surveys-template-item${
                                                selectedTemplateId === template.id ? ' is-selected' : ''
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="surveyTemplate"
                                                value={template.id}
                                                checked={selectedTemplateId === template.id}
                                                onChange={() => setSelectedTemplateId(template.id)}
                                            />
                                            <span>
                                                <span className="fw-normal d-block">
                                                    {template.title || 'Untitled template'}
                                                </span>
                                                <span className="text-muted small">
                                                    {template.category || 'Uncategorized'}
                                                    {template.description
                                                        ? ` · ${template.description.slice(0, 80)}`
                                                        : ''}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="surveys-modal__footer">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                disabled={installing}
                                onClick={() => setShowInstallModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary rounded-pill"
                                disabled={!selectedTemplateId || installing}
                                onClick={handleInstall}
                            >
                                {installing ? 'Installing…' : 'Install as draft'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Surveys;
